import React, { useEffect, useState } from 'react'
import {
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CustomTablePagination from './CustomTablePagination'
import CustomTableHeaderCell from './CustomTableHeaderCell'
import { fontSizes } from '../Typography/TypographyStyles'

export interface Column {
  field: string
  headerName: string
  headerAlign?: 'left' | 'right' | 'center'
  bodyAlignment?: 'center' | 'right' | 'left'
  width?: string | number
  renderCell?: (value: any, item: any) => React.ReactNode
  renderFormat?: (value: any) => React.ReactNode
  sortable?: boolean
}

interface TableTemplateProps {
  columns: Column[]
  datas: any[]
  refreshTable?: boolean
  state: any
  uniqueField: string
  onSelectionChange?: (selected: any[]) => void
  customRowsPerPageOptions?: Array<{
    id: string
    label: string
    value: number
  }>
  defaultRowsPerPage?: number
  onRowClick?: (item: any) => void
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  overflowX: 'auto', // Always enable horizontal scrolling
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    overflowX: 'auto', // Ensures horizontal scrolling on tablets and smaller
  },
  [theme.breakpoints.up('xl')]: {
    overflowX: 'initial', // Only disable on very large screens
  },
}))

const TableTemplate: React.FC<TableTemplateProps> = ({
  columns,
  datas,
  refreshTable,
  state,
  uniqueField,
  onSelectionChange,
  customRowsPerPageOptions,
  defaultRowsPerPage = 5,
  onRowClick,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<string>('')
  const [selected, setSelected] = useState<any[]>([]) // Store selected data objects

  useEffect(() => {
    setPage(0)
  }, [state.loading, refreshTable])

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
    setPage(newPage)

  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>) => {
    const newRowsPerPage = parseInt(event.target.value as string, 10) // Cast value to string and parse
    setRowsPerPage(newRowsPerPage)
    setPage(0) // Reset the page to 0 when rows per page changes
  }

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const stableSort = (array: any[], comparator: (a: any, b: any) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [any, number])
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      return order !== 0 ? order : a[1] - b[1]
    })
    return stabilizedThis.map((el) => el[0])
  }

  const getComparator = (order: 'asc' | 'desc', orderBy: string) => {
    return order === 'desc'
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy)
  }

  const descendingComparator = (a: any, b: any, orderBy: string) => {
    // Handle nested object properties (e.g., supervisor.name)
    const getValue = (obj: any, path: string) => {
      const parts = path.split('.')
      let value = obj
      for (const part of parts) {
        value = value?.[part]
      }
      return value
    }

    const valueA = getValue(a, orderBy)
    const valueB = getValue(b, orderBy)

    if (typeof valueA === 'string' || typeof valueB === 'string') {
      const trimmedA = String(valueA || '').trim()
      const trimmedB = String(valueB || '').trim()
      return trimmedB.localeCompare(trimmedA, undefined, {
        sensitivity: 'base',
      })
    }
    return valueB < valueA ? -1 : valueB > valueA ? 1 : 0
  }

  const visibleData = stableSort(datas, getComparator(order, orderBy)).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  )

  const allOption = {
    id: 'all-option',
    label: 'All',
    value: datas.length === 0 ? -1 : datas.length,
  }

  const defaultOptions = [
    { id: 'five-per-page-option', label: '5', value: 5 },
    { id: 'ten-per-page-option', label: '10', value: 10 },
    { id: 'twenty-five-per-page-option', label: '25', value: 25 },
  ]

  const rowsPerPageOptions = [allOption, ...(customRowsPerPageOptions || defaultOptions)]

  const isSelected = (item: any) =>
    selected.some((selectedItem) => selectedItem[uniqueField] === item[uniqueField])

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selected)
    }
  }, [selected, onSelectionChange])

  const handleSelectAllClick = () => {
    // If there are currently selected items, clear all selections
    // If there are no selected items, select all visible data
    if (numSelected > 0) {
      setSelected([])
    } else {
      setSelected(visibleData)
    }
  }

  const handleClick = (_event: any, item: any) => {
    const isItemSelected = isSelected(item)
    let newSelected: any[] = []

    if (isItemSelected) {
      newSelected = selected.filter((selectedItem) => selectedItem[uniqueField] !== item[uniqueField])
    } else {
      newSelected = [...selected, item]
    }

    setSelected(newSelected)
  }

  const numSelected = selected.length

  return (
    <Paper
      sx={{
        width: 'auto',
        mb: 2,
        boxShadow: 'none',
      }}
    >
      <StyledTableContainer>
        <Table stickyHeader sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <CustomTableHeaderCell
                  key={column.field}
                  column={column}
                  order={order}
                  orderBy={orderBy}
                  handleRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  numSelected={numSelected}
                  rowCount={rowsPerPage}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {state.loading ? (
              <TableRow style={{ height: 500 }}>
                <TableCell colSpan={columns.length + 1} align='center'>
                  <Typography color='primary'>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : state.error ? (
              <TableRow style={{ height: 500 }}>
                <TableCell colSpan={columns.length + 1} align='center'>
                  <Typography color='error'>{`Error: ${state.error}`}</Typography>
                </TableCell>
              </TableRow>
            ) : visibleData.length === 0 ? (
              <TableRow style={{ height: 500 }}>
                <TableCell colSpan={columns.length + 1} align='center'>
                  No Data Found
                </TableCell>
              </TableRow>
            ) : (
              visibleData.map((item) => (
                <Fade in={true} key={item[uniqueField]}>
                  <TableRow tabIndex={-1} selected={isSelected(item)} onClick={() => onRowClick?.(item)}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.bodyAlignment || 'center'}
                        sx={{
                          py: 2,
                          fontSize: fontSizes.size12,
                          whiteSpace: 'nowrap', // Prevents text from wrapping
                        }}
                        style={{ width: column.width || 'auto' }} // Ensure width is controlled
                      >
                        {column.field === 'selectBox' ? (
                          <Checkbox
                            onClick={(event) => handleClick(event, item)}
                            checked={isSelected(item)}
                            inputProps={{
                              'aria-labelledby': `checkbox-${item[uniqueField]}`,
                            }}
                          />
                        ) : column.renderCell ? (
                          column.renderCell(item[column.field], item)
                        ) : column.renderFormat ? (
                          column.renderFormat(item[column.field])
                        ) : (
                          item[column.field]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </Fade>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <CustomTablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        rowsPerPage={rowsPerPage}
        page={page}
        count={datas.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}

export default TableTemplate
